import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useIntl } from 'react-intl'
import { APIResponse } from '../../../../lib/commons/api'
import store from '../../../../store'
import { doNotRequireSave } from '../../../../store/requiredSaveData'
import {
  getProgramProjectReport,
  saveProgramProjectReport,
} from '../../../../infrastructure/api/program-project-reports'
import { addGlobalMessage, MessageLevel } from '../../../../store/messages'
import { AggregateField } from '../../../../domain/entity/WbsItemEntity'
import {
  deriveSavedAggregateField,
  ProgramProjectReportRow,
  toProgramProjectReportRows,
  toSaveProgramProjectReportRequest,
} from '../model/reportRow'

type SaveOptions = { clearProgress?: boolean }

type UseProgramProjectReportDataReturn = {
  data: ProgramProjectReportRow[]
  loading: boolean
  fetch: () => Promise<void>
  save: (
    currentAggregateField: AggregateField,
    options?: SaveOptions
  ) => Promise<APIResponse>
  savedAggregateField: AggregateField | null
}

type Snapshot = {
  originalRows: ProgramProjectReportRow[]
  savedAggregateField: AggregateField | null
}

export const useProgramProjectReportData =
  (): UseProgramProjectReportDataReturn => {
    const dispatch = useDispatch()
    const intl = useIntl()
    const [data, setData] = useState<ProgramProjectReportRow[]>([])
    const [loading, setLoading] = useState(true)
    const [savedAggregateField, setSavedAggregateField] =
      useState<AggregateField | null>(null)

    const dataRef = useRef<ProgramProjectReportRow[]>([])
    dataRef.current = data
    const snapshotRef = useRef<Snapshot>({
      originalRows: [],
      savedAggregateField: null,
    })

    const fetch = useCallback(async () => {
      setLoading(true)
      try {
        const dto = await getProgramProjectReport()
        const savedField = dto ? deriveSavedAggregateField(dto) : null
        const rows = dto
          ? toProgramProjectReportRows(
              dto,
              savedField ?? AggregateField.WBS_ITEM_WORKLOAD
            )
          : []
        setData(rows)
        setSavedAggregateField(savedField)
        snapshotRef.current = {
          originalRows: rows.map(row => structuredClone(row)),
          savedAggregateField: savedField,
        }
        store.dispatch(doNotRequireSave())
      } catch {
        dispatch(
          addGlobalMessage({
            type: MessageLevel.ERROR,
            title: intl.formatMessage({ id: 'global.error.systemError' }),
          })
        )
      } finally {
        setLoading(false)
      }
    }, [dispatch, intl])

    const save = useCallback(
      (
        currentAggregateField: AggregateField,
        options?: SaveOptions
      ): Promise<APIResponse> => {
        const snapshot = snapshotRef.current
        return saveProgramProjectReport(
          toSaveProgramProjectReportRequest(
            dataRef.current,
            snapshot.originalRows,
            currentAggregateField,
            snapshot.savedAggregateField,
            options
          )
        )
      },
      []
    )

    useEffect(() => {
      fetch()
    }, [fetch])

    return { data, loading, fetch, save, savedAggregateField }
  }
